import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button } from 'rebass';
import { Label } from '@rebass/forms';
import SenderProfileSelect from 'components/SenderProfileSelect';
import { EmailCcTokenizer } from 'components/EmailCcTokenizer';

/**
 * Determines which sender profile to use and whether to show the profile selector.
 *
 * Selection Logic:
 * 1. If the study has a selected sender profile, use that as the default (studySenderProfile).
 * 2. Otherwise, if the current user's email matches one of the available sender profiles,
 *    automatically use that profile (userSenderProfile).
 * 3. If no match is found, fall back to the first available sender profile.
 * 4. Show the sender profile dropdown when there are multiple sender profiles available.
 *    This allows users to override the default if needed.
 *
 * @param {Array} senderProfiles - Available sender profiles with email and id properties.
 * @param {string} userEmail - Current user's email address.
 * @param {number|string} [studySenderProfileId] - Optional study's default sender profile ID.
 * @returns {Object} Object containing userSenderProfile, studySenderProfile, defaultSenderProfile, and showSenderProfileSelect.
 */
export const getSenderProfileSelection = (senderProfiles, userEmail, studySenderProfileId) => {
    const studySenderProfile = studySenderProfileId
        ? senderProfiles.find(senderProfile => senderProfile.id == studySenderProfileId)
        : null;
    const userSenderProfile = senderProfiles.find(senderProfile => senderProfile.email === userEmail);
    const defaultSenderProfile = studySenderProfile || userSenderProfile || senderProfiles?.[0];
    const showSenderProfileSelect = senderProfiles.length > 1;

    return { userSenderProfile, studySenderProfile, defaultSenderProfile, showSenderProfileSelect };
};

/**
 * SendEmailForm is a functional component used to render a form for sending emails.
 *
 * @param {Object} props - The properties object.
 * @param {Function} props.onSubmit - A callback function to handle form submission.
 * @param {Array<string>} [props.defaultValue] - An optional array of default CC recipient values.
 * @param {Array} props.senderProfiles - Available sender profiles.
 * @param {string} [props.userEmail] - Current user's email for default selection.
 * @param {number|string} [props.studySenderProfileId] - Study's default sender profile ID.
 * @param {boolean} [props.showCcField=true] - Whether to show the CC recipients field.
 * @returns {JSX.Element} The rendered form component.
 */
const SendEmailForm = ({
    onSubmit,
    defaultValue,
    senderProfiles,
    userEmail,
    studySenderProfileId,
    showCcField = true
}) => {
    const [recipientsCc, setRecipientsCc] = useState(defaultValue || []);
    const userHasSelectedProfile = useRef(false);

    const { defaultSenderProfile, showSenderProfileSelect } = useMemo(
        () => getSenderProfileSelection(senderProfiles, userEmail, studySenderProfileId),
        [senderProfiles, userEmail, studySenderProfileId]
    );

    const [fromEmail, setFromEmail] = useState(defaultSenderProfile?.email);

    // Sync fromEmail when defaultSenderProfile changes, but only if user hasn't manually selected a profile
    useEffect(() => {
        if (!userHasSelectedProfile.current) {
            setFromEmail(defaultSenderProfile?.email);
        }
    }, [defaultSenderProfile?.email]);

    const submit = event => {
        event.preventDefault();
        onSubmit({ recipientsCc, fromEmail });
    };

    return (
        <form onSubmit={submit}>
            <Box mb={3}>
                <Label htmlFor="sender-profile-select">From</Label>
                {showSenderProfileSelect ? (
                    <SenderProfileSelect
                        id="sender-profile-select"
                        style={{ maxWidth: '100%' }}
                        senderProfiles={senderProfiles}
                        defaultValue={defaultSenderProfile?.id}
                        onChange={id => {
                            userHasSelectedProfile.current = true;
                            const senderProfile = senderProfiles.find(sp => sp.id === id);
                            setFromEmail(senderProfile.email);
                        }}
                    />
                ) : (
                    <Box sx={{ padding: '8px 0', color: 'text' }}>
                        {defaultSenderProfile?.name ? (
                            <>
                                {defaultSenderProfile.name} &lt;{defaultSenderProfile.email}&gt;
                            </>
                        ) : (
                            defaultSenderProfile?.email
                        )}
                    </Box>
                )}
            </Box>
            {showCcField && (
                <>
                    <Label htmlFor="recipients-cc-input">CC Recipients</Label>
                    <EmailCcTokenizer
                        id="recipients-cc-input"
                        addCcButtonVariant="secondary-gray"
                        addButtonVariant="secondary"
                        className="mb-4"
                        value={recipientsCc}
                        onChange={value => setRecipientsCc(value)}
                    />
                </>
            )}
            <div className="flex justify-end">
                <Button type="submit">Send</Button>
            </div>
        </form>
    );
};

export default SendEmailForm;
